import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface LocationSuggestion {
  description: string;
  place_id: string;
}

export const useLocationValidation = () => {
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const searchLocations = async (input: string) => {
    if (input.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('validate-location', {
        body: { input, type: 'autocomplete' }
      });

      if (error) throw error;
      setSuggestions(data?.predictions || []);
    } catch (error) {
      console.error('Error searching locations:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const validateLocation = async (location: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('validate-location', {
        body: { input: location, type: 'validate' }
      });

      if (error) throw error;
      return data?.valid || false;
    } catch (error) {
      console.error('Error validating location:', error);
      return false;
    }
  };

  const searchAddresses = async (input: string) => {
    if (input.length < 5) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('validate-location', {
        body: { input, type: 'address' }
      });

      if (error) throw error;
      setSuggestions(data?.predictions || []);
    } catch (error) {
      console.error('Error searching addresses:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const geocodeAddress = async (placeId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('validate-location', {
        body: { input: placeId, type: 'geocode' }
      });

      if (error) throw error;
      return {
        latitude: data.latitude,
        longitude: data.longitude,
        formatted_address: data.formatted_address,
        address_components: data.address_components
      };
    } catch (error) {
      console.error('Error geocoding address:', error);
      return null;
    }
  };

  return {
    suggestions,
    isLoading,
    searchLocations,
    validateLocation,
    searchAddresses,
    geocodeAddress,
  };
};
